import { useCallback, useMemo } from 'react'

import { HookedRecordResult } from '@ingestro/importer-react'
import { UniversalImporterValueObjectsImportTypeEnum } from 'generated/resources/datatable.graphql'
import {
  SchemaDataFieldsDocument,
  SchemaDataFieldsQuery,
  TargetsDataDocument,
  TargetsDataQuery,
  UniversalImporterImportProcessMetadataInput,
} from 'generated/resources/graphql'

import { useLazyQuery, useQuery } from 'modules/core/hooks/useQuery'

import { type EditionData } from './types'

type UseEditionDataProps = {
  companyId: string
  importType: UniversalImporterValueObjectsImportTypeEnum
  metadata: UniversalImporterImportProcessMetadataInput[]
  isEdition: boolean
}

interface UseEditionDataResult {
  editionData: EditionData
  loading: boolean
  error?: Error
  fetchEditionDataForIds: (
    identifierEntries: { key: string; type: 'string' | 'integer'; values: string[] }[]
  ) => Promise<EditionData>
}

function processValue(value: string | null | undefined) {
  if (typeof value === 'string' && value.startsWith('[') && value.endsWith(']')) {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed) && parsed.every((option) => typeof option === 'string')) {
      return parsed
    } else {
      return value
    }
  }
  return value
}

function buildEditionData(
  schemaDataQuery: TargetsDataQuery | undefined
): EditionData {
  if (!schemaDataQuery?.universalImporter.schemaData) {
    return []
  }

  return schemaDataQuery.universalImporter.schemaData.map((item) => {
    const record: Record<string, string | string[] | HookedRecordResult> = {}
    item.fields?.forEach(({ key, value }) => {
      if (key) {
        if (key === 'errors' && value) {
          record[key] = {
            value: value,
            info: [
              {
                message: String(value),
                level: 'info',
              },
            ],
          }
        } else {
          record[key] = processValue(value) ?? ''
        }
      }
    })
    return record
  })
}

function getFieldNames(
  schemaDataFieldsQuery: SchemaDataFieldsQuery | undefined
): string[] {
  return (
    schemaDataFieldsQuery?.universalImporter.schemaAdapters.map((item) => item.key) ?? []
  )
}

export const useEditionData = ({
  companyId,
  importType,
  metadata,
  isEdition,
}: UseEditionDataProps): UseEditionDataResult => {
  const {
    data: schemaDataFieldsQuery,
    loading: schemaDataFieldsLoading,
    error: schemaError,
  } = useQuery(SchemaDataFieldsDocument, {
    variables: {
      companyId,
      importType,
      metadata,
    },
    skip: !isEdition,
  })

  const fieldNames = useMemo(
    () => getFieldNames(schemaDataFieldsQuery),
    [schemaDataFieldsQuery]
  )

  const {
    data: schemaDataQuery,
    loading: targetsLoading,
    error: targetsError,
  } = useQuery(TargetsDataDocument, {
    variables: {
      fieldNames,
      importType,
      metadata,
    },
    skip: schemaDataFieldsLoading || !schemaDataFieldsQuery || !isEdition,
  })

  const editionData = useMemo(
    () => buildEditionData(schemaDataQuery),
    [schemaDataQuery]
  )

  const [fetchSchemaFields] = useLazyQuery(SchemaDataFieldsDocument)
  const [fetchTargetsData] = useLazyQuery(TargetsDataDocument)

  const fetchEditionDataForIds = useCallback(
    async (
      identifierEntries: { key: string; type: 'string' | 'integer'; values: string[] }[]
    ): Promise<EditionData> => {
      const dynamicMetadata: UniversalImporterImportProcessMetadataInput[] = [
        ...metadata,
        ...identifierEntries.map((entry) => ({
          key: entry.key,
          type: entry.type,
          values: entry.values,
        })),
      ]

      const { data: schemaFieldsData } = await fetchSchemaFields({
        variables: { companyId, importType, metadata: dynamicMetadata },
      })

      const names = getFieldNames(schemaFieldsData)

      const { data: targetsData } = await fetchTargetsData({
        variables: { fieldNames: names, importType, metadata: dynamicMetadata },
      })

      return buildEditionData(targetsData)
    },
    [companyId, importType, metadata, fetchSchemaFields, fetchTargetsData]
  )

  return {
    editionData,
    loading: isEdition ? schemaDataFieldsLoading || targetsLoading : false,
    error: isEdition ? schemaError || targetsError : undefined,
    fetchEditionDataForIds,
  }
}
